import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Heart, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch {
        // keep polling
      }
      attempts += 1;
      if (attempts < 8) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="max-w-lg mx-auto px-4 sm:px-6 py-24">
      <div className="card-pain p-10 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-amber-400 animate-spin" />
            <h1 className="font-display mt-6 text-2xl font-semibold text-slate-50">
              Confirming your donation...
            </h1>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-amber-400" data-testid="payment-paid-icon" />
            <h1 className="font-display mt-6 text-2xl sm:text-3xl font-semibold text-slate-50">
              Thank you for standing with us.
            </h1>
            <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed">
              Your donation has been received and goes directly to frontline patrols, court support,
              and survivor programs. You are part of breaking the silence.
            </p>
          </>
        )}
        {status === "pending" && (
          <>
            <Heart className="mx-auto h-12 w-12 text-amber-400" />
            <h1 className="font-display mt-6 text-2xl font-semibold text-slate-50">
              Donation processing
            </h1>
            <p className="mt-4 text-sm text-slate-300 leading-relaxed">
              Your payment is being confirmed by our provider. It will appear as complete shortly.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-red-400" />
            <h1 className="font-display mt-6 text-2xl font-semibold text-slate-50">
              Something went wrong
            </h1>
            <p className="mt-4 text-sm text-slate-300">
              We could not confirm this donation session. If funds were taken, please contact us.
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-return-home-link"
          className="btn-gold-outline mt-8 inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-amber-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Return Home
        </Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="max-w-lg mx-auto px-4 sm:px-6 py-24">
      <div className="card-pain p-10 text-center">
        <XCircle className="mx-auto h-12 w-12 text-slate-400" />
        <h1 className="font-display mt-6 text-2xl sm:text-3xl font-semibold text-slate-50">
          Donation cancelled
        </h1>
        <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed">
          No charge was made. The frontline is here whenever you are ready to stand with us.
        </p>
        <Link
          to="/"
          data-testid="payment-cancel-return-link"
          className="btn-royal mt-8 inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Return Home
        </Link>
      </div>
    </div>
  );
}
